import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";

let LOG_URL;
if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
    LOG_URL = "http://localhost:8001/log";
} else {
    LOG_URL = window.location.origin + "/1/log";
}

export default function LogsPage() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedDeviceId, setSelectedDeviceId] = useState(null);
    const [expandedLogs, setExpandedLogs] = useState(new Set());
    const [clearing, setClearing] = useState(false);

    const fetchLogs = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(LOG_URL);
            if (!res.ok) {
                throw new Error(`Failed to fetch logs: ${res.statusText}`);
            }
            const data = await res.json();
            // Sort logs by timestamp descending
            const sortedLogs = data.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
            setLogs(sortedLogs);
            
            // Auto-select first device if none selected
            if (sortedLogs.length > 0 && !selectedDeviceId) {
                const uniqueDevices = [...new Set(sortedLogs.map(l => l.deviceId))];
                if (uniqueDevices.length > 0) {
                    setSelectedDeviceId(uniqueDevices[0]);
                }
            }
        } catch (err) {
            console.error("LogsPage fetch error:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const clearLogs = async () => {
        if (!window.confirm("Are you sure you want to delete all stored logs on the server?")) {
            return;
        }
        setClearing(true);
        try {
            const res = await fetch(LOG_URL, { method: "DELETE" });
            if (!res.ok) {
                throw new Error(`Failed to clear logs: ${res.statusText}`);
            }
            setLogs([]);
            setSelectedDeviceId(null);
            setExpandedLogs(new Set());
        } catch (err) {
            alert(`Error: ${err.message}`);
        } finally {
            setClearing(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, []);

    // Group logs by deviceId
    const devices = useMemo(() => {
        const groups = {};
        logs.forEach(log => {
            const devId = log.deviceId || "UNKNOWN";
            if (!groups[devId]) {
                groups[devId] = {
                    deviceId: devId,
                    clientIp: log.clientIp || "Unknown",
                    userAgent: log.userAgent || "Unknown",
                    logs: [],
                    errors: 0,
                    warnings: 0
                };
            }
            groups[devId].logs.push(log);
            if (log.level === "ERROR" || log.level === "FATAL") {
                groups[devId].errors++;
            } else if (log.level === "WARN") {
                groups[devId].warnings++;
            }
        });
        return Object.values(groups);
    }, [logs]);

    const activeDevice = useMemo(() => {
        return devices.find(d => d.deviceId === selectedDeviceId) || null;
    }, [devices, selectedDeviceId]);

    const toggleExpandLog = (index) => {
        const next = new Set(expandedLogs);
        if (next.has(index)) {
            next.delete(index);
        } else {
            next.add(index);
        }
        setExpandedLogs(next);
    };

    // Helper functions for user agent parsing
    const getBrowserInfo = (ua) => {
        if (!ua) return "Unknown";
        if (ua.includes("Firefox/")) return "Firefox";
        if (ua.includes("Vanadium/")) return "Vanadium (GrapheneOS)";
        if (ua.includes("Chrome/")) return "Chrome";
        if (ua.includes("Safari/") && !ua.includes("Chrome")) return "Safari";
        if (ua.includes("Edge/")) return "Edge";
        return ua.split(" ")[0] || "Unknown";
    };

    const getOSInfo = (ua) => {
        if (!ua) return "Unknown";
        if (ua.includes("Android")) return "Android";
        if (ua.includes("iPhone") || ua.includes("iPad")) return "iOS";
        if (ua.includes("Windows")) return "Windows";
        if (ua.includes("Macintosh")) return "macOS";
        if (ua.includes("Linux")) return "Linux";
        return "Unknown";
    };

    return (
        <div className="min-h-screen text-slate-100 p-6 md:p-12" style={{ backgroundColor: "#050520" }}>
            {/* Header */}
            <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between border-b border-white/10 pb-6 mb-8 gap-4">
                <div>
                    <div className="flex items-center gap-3">
                        <Link to="/1/" className="text-white/60 hover:text-white transition-colors">
                            <i className="bi bi-arrow-left text-xl"></i>
                        </Link>
                        <h1 className="text-2xl md:text-3xl font-bold tracking-tight bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">
                            System Diagnostics
                        </h1>
                    </div>
                    <p className="text-slate-400 text-sm mt-1">
                        View client-side logs, exceptions, and warnings captured from devices.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={fetchLogs}
                        disabled={loading}
                        className="px-4 py-2 bg-white/5 hover:bg-white/10 active:bg-white/15 border border-white/10 rounded-xl transition-all duration-150 flex items-center gap-2 text-sm disabled:opacity-50"
                    >
                        <i className={`bi bi-arrow-clockwise ${loading ? 'animate-spin' : ''}`}></i>
                        Refresh
                    </button>
                    <button
                        onClick={clearLogs}
                        disabled={clearing || logs.length === 0}
                        className="px-4 py-2 bg-red-500/10 hover:bg-red-500/20 active:bg-red-500/30 border border-red-500/20 text-red-400 rounded-xl transition-all duration-150 flex items-center gap-2 text-sm disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                        <i className="bi bi-trash3"></i>
                        Clear Logs
                    </button>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="max-w-7xl mx-auto mb-6 p-4 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl flex items-center gap-3">
                    <i className="bi bi-exclamation-triangle-fill text-xl"></i>
                    <div>
                        <h4 className="font-semibold text-sm">Failed to connect to logging server</h4>
                        <p className="text-xs opacity-80">{error}</p>
                    </div>
                </div>
            )}

            {/* Main Content Dashboard */}
            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Device List Sidebar */}
                <div className="lg:col-span-1 flex flex-col gap-4">
                    <h3 className="text-sm font-semibold tracking-wider uppercase text-slate-400 px-1">
                        Connected Devices ({devices.length})
                    </h3>
                    <div className="flex flex-col gap-3 max-h-[70vh] overflow-y-auto pr-1">
                        {devices.length === 0 ? (
                            <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center text-slate-400 text-sm">
                                {loading ? "Loading devices..." : "No logs recorded yet."}
                            </div>
                        ) : (
                            devices.map(device => {
                                const isSelected = device.deviceId === selectedDeviceId;
                                return (
                                    <button
                                        key={device.deviceId}
                                        onClick={() => setSelectedDeviceId(device.deviceId)}
                                        className={`w-full text-left p-4 rounded-2xl border transition-all duration-200 flex flex-col gap-2 ${
                                            isSelected
                                                ? "bg-indigo-600/10 border-indigo-500/50 shadow-[0_0_15px_rgba(99,102,241,0.15)] text-white"
                                                : "bg-white/5 border-white/10 hover:bg-white/10 text-slate-300"
                                        }`}
                                    >
                                        <div className="flex items-center justify-between w-full">
                                            <span className="font-mono text-xs opacity-75 font-semibold">
                                                {device.deviceId.substring(0, 15)}...
                                            </span>
                                            <div className="flex gap-1.5">
                                                {device.errors > 0 && (
                                                    <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-red-500/20 text-red-400 border border-red-500/20">
                                                        {device.errors} Err
                                                    </span>
                                                )}
                                                {device.warnings > 0 && (
                                                    <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/20">
                                                        {device.warnings} Warn
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="text-xs text-slate-400 flex flex-wrap gap-x-2 gap-y-1">
                                            <span className="flex items-center gap-1">
                                                <i className="bi bi-laptop"></i>
                                                {getOSInfo(device.userAgent)} • {getBrowserInfo(device.userAgent)}
                                            </span>
                                            <span>|</span>
                                            <span className="flex items-center gap-1">
                                                <i className="bi bi-globe"></i>
                                                {device.clientIp}
                                            </span>
                                        </div>
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* Log Details Panel */}
                <div className="lg:col-span-2 flex flex-col gap-4">
                    <h3 className="text-sm font-semibold tracking-wider uppercase text-slate-400 px-1">
                        Log Stream {activeDevice ? `(${activeDevice.logs.length} logs)` : ""}
                    </h3>
                    
                    {!activeDevice ? (
                        <div className="flex-1 min-h-[40vh] bg-white/5 border border-white/10 rounded-2xl flex flex-col items-center justify-center text-slate-400 p-8 text-center">
                            <i className="bi bi-terminal text-5xl mb-4 opacity-30 text-indigo-400"></i>
                            <h4 className="font-semibold text-lg text-slate-200">No Logs Selected</h4>
                            <p className="text-sm max-w-sm mt-1">
                                Select a device from the left sidebar to inspect its timeline and diagnostic stream.
                            </p>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-4 max-h-[75vh] overflow-y-auto pr-1">
                            {activeDevice.logs.map((log, index) => {
                                const isExpanded = expandedLogs.has(index);
                                const isError = log.level === "ERROR" || log.level === "FATAL";
                                const isWarn = log.level === "WARN";
                                
                                return (
                                    <div
                                        key={index}
                                        className={`p-4 rounded-2xl border transition-colors ${
                                            isError 
                                                ? "bg-red-500/5 border-red-500/20 hover:border-red-500/30" 
                                                : isWarn 
                                                    ? "bg-amber-500/5 border-amber-500/20 hover:border-amber-500/30" 
                                                    : "bg-white/5 border-white/10 hover:border-white/15"
                                        }`}
                                    >
                                        {/* Log Item Header */}
                                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 border-b border-white/5 pb-2.5 mb-2.5">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className={`px-2 py-0.5 text-[10px] font-bold tracking-wide rounded-md uppercase ${
                                                    log.level === "FATAL" 
                                                        ? "bg-red-600 text-white" 
                                                        : isError 
                                                            ? "bg-red-500/20 text-red-400 border border-red-500/30" 
                                                            : isWarn 
                                                                ? "bg-amber-500/20 text-amber-400 border border-amber-500/30" 
                                                                : "bg-slate-500/20 text-slate-300 border border-slate-500/30"
                                                }`}>
                                                    {log.level}
                                                </span>
                                                <span className="text-[11px] font-mono text-slate-400">
                                                    {new Date(log.timestamp).toLocaleString()}
                                                </span>
                                            </div>
                                            <span className="text-[11px] text-slate-400 truncate max-w-xs md:max-w-md font-mono" title={log.url}>
                                                URL: {new URL(log.url).pathname + new URL(log.url).hash}
                                            </span>
                                        </div>

                                        {/* Message */}
                                        <div className="text-sm font-mono break-words text-slate-200 whitespace-pre-wrap">
                                            {log.message}
                                        </div>

                                        {/* Stack Trace Toggle */}
                                        {log.stack && (
                                            <div className="mt-3">
                                                <button
                                                    onClick={() => toggleExpandLog(index)}
                                                    className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1.5 transition-colors"
                                                >
                                                    <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                                                    {isExpanded ? "Hide Stack Trace" : "Show Stack Trace"}
                                                </button>
                                                
                                                {isExpanded && (
                                                    <pre className="mt-2.5 p-3.5 bg-black/40 border border-white/5 rounded-xl text-[11px] font-mono text-red-300/90 overflow-x-auto whitespace-pre leading-relaxed shadow-inner">
                                                        {log.stack}
                                                    </pre>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
